// Google Analytics 4 & Microsoft Clarity Analytics Utilities

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    clarity?: (...args: any[]) => void;
  }
}

/**
 * Debug mode helper - checks if analytics should be disabled
 * Enable: Visit site with ?debug=true
 * Disable: Visit site with ?debug=false
 */
export const isDebugMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  
  try {
    const hostname = window.location.hostname;
    const isPreview = hostname.includes('preview');
    const urlParams = new URLSearchParams(window.location.search);
    const urlParam = urlParams.get('debug') === 'true' || urlParams.get('test') === 'true';
    const sessionFlag = sessionStorage.getItem('debug_mode_session') === 'true';
    const localFlag = localStorage.getItem('debug_mode') === 'true';
    
    const result = isPreview || urlParam || sessionFlag || localFlag;
    
    // Save to storage if URL param is present
    if (urlParam) {
      localStorage.setItem('debug_mode', 'true');
      sessionStorage.setItem('debug_mode_session', 'true');
    }
    
    // Handle explicit disable
    if (urlParams.get('debug') === 'false') {
      localStorage.removeItem('debug_mode');
      sessionStorage.removeItem('debug_mode_session');
      return false;
    }
    
    return result;
  } catch {
    return false;
  }
};

// Custom event names
export const AnalyticsEvents = {
  SCAN_STARTED: 'scan_started',
  SCAN_COMPLETED: 'scan_completed',
  SCAN_FAILED: 'scan_failed',
  
  PAGE_VIEW: 'page_view',
  DEEP_SCAN_CTA_CLICKED: 'deep_scan_cta_clicked',
  TECHNICAL_AUDIT_CLICKED: 'technical_audit_clicked',
  WAITLIST_SIGNUP: 'waitlist_signup',
} as const;

type AnalyticsEventName = typeof AnalyticsEvents[keyof typeof AnalyticsEvents];

interface EventParams {
  brand_name?: string;
  keyword?: string;
  visibility_score?: number;
  chatgpt_found?: boolean;
  google_found?: boolean;
  page_path?: string;
  page_title?: string;
  [key: string]: any;
}

/**
 * Track a custom event with Google Analytics 4
 */
export const trackEvent = (eventName: AnalyticsEventName, params?: EventParams) => {
  if (isDebugMode()) {
    return;
  }
  
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }
  
  try {
    window.gtag('event', eventName, params);
  } catch (error) {
    console.error('Error sending event to GA4:', error);
  }
};

/**
 * Track a manual page view for virtual navigation (shallow routing)
 * Useful for SPAs where URL changes don't trigger full page loads
 */
export const trackPageView = (path: string, title?: string) => {
  if (isDebugMode()) {
    return;
  }
  
  if (typeof window === 'undefined') return;
  
  // GA4 page view
  if (window.gtag) {
    window.gtag('config', 'G-NXTJSEG24Z', {
      page_path: path,
      page_title: title || document.title,
    });
  }
  
  // Microsoft Clarity - trigger new page view
  if (window.clarity) {
    window.clarity('set', 'page', path);
  }
};

/**
 * Scroll smoothly to the Pro Waitlist section and focus the email input.
 * Retries briefly in case the element is still animating into the DOM.
 * If #pro-waitlist doesn't exist on the current page, navigates to "/" first.
 */
export const scrollToWaitlist = () => {
  const scrollAndFocus = () => {
    const el = document.getElementById('pro-waitlist');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        const input = el.querySelector<HTMLInputElement>('input[type="email"]');
        input?.focus();
      }, 600);
      return true;
    }
    return false;
  };

  if (scrollAndFocus()) return;

  // Retry once after a short delay (entrance animation may not have rendered yet)
  setTimeout(() => {
    if (scrollAndFocus()) return;
    window.location.replace('/#pro-waitlist');
  }, 700);
};

/**
 * Track scan started event
 */
export const trackScanStarted = (brandName: string, keyword: string, url?: string) => {
  trackEvent(AnalyticsEvents.SCAN_STARTED, {
    brand_name: brandName || 'Unknown Brand',
    keyword: keyword || 'Unknown Keyword',
    website_url: url || 'Not provided',
  });
};

/**
 * Track scan completed event
 */
export const trackScanCompleted = (
  brandName: string, 
  keyword: string, 
  visibilityScore: number,
  chatgptFound: boolean,
  googleFound: boolean,
  url?: string
) => {
  trackEvent(AnalyticsEvents.SCAN_COMPLETED, {
    brand_name: brandName || 'Unknown Brand',
    keyword: keyword || 'Unknown Keyword',
    visibility_score: visibilityScore,
    chatgpt_found: chatgptFound,
    google_found: googleFound,
    website_url: url || 'Not provided',
  });
};


/**
 * Track deep scan CTA clicked event (locked/coming soon features)
 */
export const trackDeepScanCtaClicked = (linkName: string) => {
  trackEvent(AnalyticsEvents.DEEP_SCAN_CTA_CLICKED, {
    link_name: linkName,
  });
};

/**
 * Track scan failed event
 */
export const trackScanFailed = (
  brandName: string,
  keyword: string,
  errorType: 'timeout' | 'network' | 'api_error' | 'unknown',
  errorMessage?: string,
  url?: string
) => {
  trackEvent(AnalyticsEvents.SCAN_FAILED, {
    brand_name: brandName || 'Unknown Brand',
    keyword: keyword || 'Unknown Keyword',
    error_type: errorType,
    error_message: errorMessage?.substring(0, 100) || 'No message',
    website_url: url || 'Not provided',
  });
};
